import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table } from "../blocks.js";
import type { WikiTopic } from "../types.js";

export const sizeBands: WikiTopic = {
  slug: "size-bands",
  title: "Size bands",
  summary:
    "Five bands in rupees, two of them SEBI's, plus a sixth chip for the names nothing can size.",
  group: "concepts",
  sections: [
    {
      heading: "The five bands",
      code: "large / mid / small / micro / nano",
      blocks: [
        table(
          "The five size bands and their rupee edges",
          ["Band", "Range"],
          [
            ["large", <Datum key="l">above ₹1,00,000 Cr</Datum>],
            ["mid", <Datum key="m">₹33,000 to 1,00,000 Cr</Datum>],
            ["small", <Datum key="s">₹5,000 to 33,000 Cr</Datum>],
            ["micro", <Datum key="i">₹500 to 5,000 Cr</Datum>],
            ["nano", <Datum key="n">below ₹500 Cr</Datum>],
          ],
        ),
        p(
          <>
            Every range is half-open, <Datum>min ≤ cap &lt; max</Datum>, so the bands tile the line
            with no gap and no overlap. A cap of exactly <Datum>₹33,000 Cr</Datum> is Mid, and it is
            Mid in the filter, in the column, and in any count either of them reports.
          </>,
        ),
        p(
          <>
            The edges are absolute rupees rather than live ranks, deliberately. A band whose edge
            moved with the market would make "large cap" mean something different on Tuesday, and a
            saved link would stop meaning what it meant when it was saved. The cost is that the
            edges need re-basing when the published cutoffs drift far enough.
          </>,
        ),
        note(
          <>
            The bands live in <Datum>MARKET_CAP_BUCKETS</Datum> in core, which is what the feed
            query filters on. The dropdown labels are a second copy, and{" "}
            <Datum>marketCapOptions.test.ts</Datum> parses the digits back out of every label and
            holds them against core. A label that says one range while the query uses another is a
            lie the reader cannot check.
          </>,
        ),
      ],
    },
    {
      heading: "Large and Mid mean what SEBI means",
      blocks: [
        p(
          <>
            The top two edges are not ours. The Master Circular for Mutual Funds as on{" "}
            <Datum>2026-03-20</Datum>, para <Datum>3.9.1</Datum>, defines large cap as the 1st to
            100th entity by full market capitalisation and mid cap as the 101st to 250th. AMFI
            republishes the ranked list every six months, within five days of period end.
          </>,
        ),
        p(
          <>
            On the list for the six months ended <Datum>2026-06-30</Datum>, published{" "}
            <Datum>2026-07-07</Datum> over <Datum>5,427</Datum> entities, rank 100 is GAIL at{" "}
            <Datum>₹1,06,346.15 Cr</Datum> and rank 250 is Godrej Industries at{" "}
            <Datum>₹33,664.21 Cr</Datum>. So <Datum>₹1,00,000 Cr</Datum> and{" "}
            <Datum>₹33,000 Cr</Datum> are round numbers chosen to sit on those two ranks.
          </>,
        ),
        p(
          <>
            They sit a little under the published boundaries rather than on them because AMFI
            averages daily full market cap over six months and across the exchanges a name is listed
            on, while these edges are applied to one session's cap. A name oscillating around rank
            100 should not change band on a single day's move.
          </>,
        ),
        note(
          <>
            Checked against our own universe on <Datum>2026-09-05</Datum>: rank 100 is INDHOTEL at{" "}
            <Datum>₹1,02,485 Cr</Datum> and rank 250 is MANAPPURAM at <Datum>₹31,910 Cr</Datum>,
            which puts <Datum>101</Datum> names in Large and <Datum>141</Datum> in Mid against the{" "}
            <Datum>100</Datum> and <Datum>150</Datum> the rule would give.
          </>,
        ),
      ],
    },
    {
      heading: "Small, micro, and nano are ours",
      blocks: [
        p(
          <>
            Below rank 250 SEBI has one bucket for five thousand companies, which is useless as a
            filter. The bottom three bands subdivide it on round numbers of our own choosing, and
            they hold <Datum>466</Datum>, <Datum>652</Datum>, and <Datum>401</Datum> names.
          </>,
        ),
        note(
          <>
            The first shipped version had these a full tier too low. <Datum>₹20,000 Cr</Datum> was
            labelled "large" when it is rank <Datum>344</Datum>, and <Datum>₹1,00,000 Cr</Datum> was
            the floor of a "mega" band when it is rank <Datum>110</Datum>. Those were roughly the
            right cutoffs around <Datum>2017</Datum>; the market has moved about fivefold since, and
            a reader who knows the SEBI convention would have been misled by every label.
          </>,
        ),
      ],
    },
    {
      heading: "Unknown is a chip, not an error",
      code: "unknown",
      blocks: [
        p(
          <>
            The sixth chip is <Datum>unknown</Datum>, and it is a real selection rather than a
            failure state. <Datum>863</Datum> of the <Datum>2,221</Datum> symbols that produced a
            signal in the last 45 sessions cannot be sized: <Datum>39%</Datum> of the feed, measured
            on prod on <Datum>2026-09-05</Datum>. Far too much to drop silently the moment a reader
            ticks one band, so naming it makes the gap something the reader chooses to include or
            exclude.
          </>,
        ),
        table(
          "Why a symbol cannot be sized",
          ["Reason", "Symbols"],
          [
            ["No usable balance sheet at all", <Datum key="a">488</Datum>],
            [
              "Files standalone only, and the pipeline reads consolidated",
              <Datum key="b">315</Datum>,
            ],
            ["Filings older than the 800-day window, plus five the ceiling rejected", "The rest"],
          ],
        ),
        p(
          <>
            The ceiling is the last of those. A filing that reports paid-up capital in the wrong
            unit does not produce a slightly wrong cap; it produces one wrong by orders of
            magnitude, and the largest real company in the country sits at{" "}
            <Datum>₹17,89,063 Cr</Datum>. Anything above the line is treated as unknown rather than
            clamped, so the reader gets "we cannot size this name", which is true, instead of a
            number that is confidently wrong.
          </>,
        ),
      ],
    },
  ],
};
